"use client";

import * as React from "react";
import { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

// ============================================================================
// SectionCard - Cards with styled headers for content sections
// ============================================================================

export interface SectionCardProps {
  /** Card title */
  title: string;
  /** Card description */
  description?: string;
  /** Icon component from lucide-react */
  icon?: LucideIcon;
  /** Header color theme */
  headerVariant?: "default" | "emerald" | "blue" | "amber" | "purple" | "rose";
  /** Right side content for header (badges, buttons, etc) */
  headerAction?: React.ReactNode;
  /** Card content */
  children: React.ReactNode;
  /** Additional className for card */
  className?: string;
  /** Additional className for content */
  contentClassName?: string;
  /** Whether card content has default padding */
  noPadding?: boolean;
}

const headerVariants = {
  default: {
    header: "bg-muted/50 border-b",
    iconBg: "bg-muted-foreground/10",
    iconColor: "text-muted-foreground",
  },
  emerald: {
    header: "bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 border-b",
    iconBg: "bg-emerald-100 dark:bg-emerald-900/50",
    iconColor: "text-emerald-600 dark:text-emerald-400",
  },
  blue: {
    header: "bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 border-b",
    iconBg: "bg-blue-100 dark:bg-blue-900/50",
    iconColor: "text-blue-600 dark:text-blue-400",
  },
  amber: {
    header: "bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border-b",
    iconBg: "bg-amber-100 dark:bg-amber-900/50",
    iconColor: "text-amber-600 dark:text-amber-400",
  },
  purple: {
    header: "bg-gradient-to-r from-purple-50 to-violet-50 dark:from-purple-950/30 dark:to-violet-950/30 border-b",
    iconBg: "bg-purple-100 dark:bg-purple-900/50",
    iconColor: "text-purple-600 dark:text-purple-400",
  },
  rose: {
    header: "bg-gradient-to-r from-rose-50 to-pink-50 dark:from-rose-950/30 dark:to-pink-950/30 border-b",
    iconBg: "bg-rose-100 dark:bg-rose-900/50",
    iconColor: "text-rose-600 dark:text-rose-400",
  },
};

export function SectionCard({
  title,
  description,
  icon: Icon,
  headerVariant = "default",
  headerAction,
  children,
  className,
  contentClassName,
  noPadding = false,
}: SectionCardProps) {
  const styles = headerVariants[headerVariant];

  return (
    <Card className={cn("overflow-hidden", className)}>
      <CardHeader className={styles.header}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <CardTitle className="flex items-center gap-2">
              {Icon && (
                <div className={cn("p-2 rounded-lg", styles.iconBg)}>
                  <Icon className={cn("h-5 w-5", styles.iconColor)} />
                </div>
              )}
              <span className="truncate">{title}</span>
            </CardTitle>
            {description && (
              <CardDescription className="mt-1.5">
                {description}
              </CardDescription>
            )}
          </div>
          {headerAction && (
            <div className="flex-shrink-0">
              {headerAction}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className={cn(
        noPadding ? "p-0" : "pt-6",
        contentClassName
      )}>
        {children}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// SimpleCard - Basic card without styled header
// ============================================================================

export interface SimpleCardProps {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  hoverable?: boolean;
}

export function SimpleCard({ 
  children, 
  className,
  onClick,
  hoverable = false,
}: SimpleCardProps) {
  const Comp = onClick ? "button" : "div";
  
  return (
    <Comp
      onClick={onClick}
      className={cn(
        "rounded-xl border bg-card p-4 text-left",
        hoverable && "transition-all hover:shadow-md hover:-translate-y-0.5",
        onClick && "cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2",
        className
      )}
    >
      {children}
    </Comp>
  );
}

